import { IDiscount, ProtocolDosingRow } from '@protoplan/types';
import { createSlice } from '@reduxjs/toolkit';
import { IAction }  from '../actionTypes';
import { alphabeticalSort } from '../../../utils/string';




export interface ProtocolTable
{
  protocolId: number,
  userId: number,
  username: string,
  name: string,
  dosings?: ProtocolDosingRow[],
  budget?: number,
  orderReminderActive: boolean,
  public: boolean,
  edit?: boolean,
  sharedWith: { userId: number, username: string, edit: boolean }[],
  currencyId: number,
  currencyCode: string,
  currencySymbol: string,
  dosingsRetrieved?: Date,
  folderId?: number,
  fees: boolean
}

export const defaultProtocolSettings = {
  dosings: [],
  orderReminderActive: true,
  public: false,
  edit: true,
  sharedWith: [],
  fees: true };

export interface Folder
{
  folderId: number,
  parentFolderId?: number,
  name: string
}

export interface ProtocolState
{
  protocols: ProtocolTable[],
  folders: Folder[],
  savePublicProtocolId?: number,
  saveMmProtocolId?: number
};

export const protocolSlice = createSlice(
  {
    name: 'protocol',
    initialState: {
      protocols: <ProtocolTable[]>[],
      folders: <Folder[]>[],
      savePublicProtocolId: undefined,
      saveMmProtocolId: undefined },
    reducers: {
      updateProtocols: (state: ProtocolState, action: IAction<ProtocolTable[]>) =>
      {
        state.protocols = action.payload;
        state.protocols.sort((a, b) => alphabeticalSort(a.name, b.name));
      },
      updateProtocolPublic: (state: ProtocolState, action: IAction<{
        protocolId: number,
        public?: boolean }>) =>
      {
        const protocol = state.protocols.find(p => p.protocolId === action.payload.protocolId);
        if(!protocol)
          return;

        protocol.public = (action.payload.public !== undefined) ? action.payload.public : !protocol.public;
      },
      addProtocol: (
        state: ProtocolState,
        action: IAction<Exclude<ProtocolTable, typeof defaultProtocolSettings>>) =>
      {
        state.protocols = state.protocols.concat(action.payload);
      },
      updateProtocolFolder: (state: ProtocolState, action: IAction<{ protocolId: number, folderId?: number }>) =>
      {
        const protocol = state.protocols.find(p => p.protocolId === action.payload.protocolId);
        if(!protocol)
          return;

        protocol.folderId = action.payload.folderId;
      },
      updateDosings: (
        state: ProtocolState,
        action: IAction<{ protocolId: number, dosings: ProtocolDosingRow[] }>) =>
      {
        const protocol = state.protocols.find(p => p.protocolId === action.payload.protocolId);
        if(!protocol)
          return;

        protocol.dosings = action.payload.dosings;
        protocol.dosingsRetrieved = new Date();

        const retrievedDosings = state.protocols.filter(p => p.dosings);
        if(retrievedDosings.length <= 10)
          return;
        const oldestDosings = retrievedDosings
          .sort((a, b) => (a.dosingsRetrieved?.getTime() || Infinity) - (b.dosingsRetrieved?.getTime() || Infinity));
        const oldestDosingsProtocol = state.protocols.find(p => p.protocolId === oldestDosings[0].protocolId);
        if(!oldestDosingsProtocol)
          return;
        oldestDosingsProtocol.dosings = undefined;
      },
      removeDosings: (state: ProtocolState) =>
      {
        state.protocols.forEach(p => p.dosings = undefined);
      },
      removeProtocol: (state: ProtocolState, action: IAction<number>) =>
      {
        state.protocols = state.protocols.filter(p => p.protocolId !== action.payload);
      },
      updateProtocolName: (state: ProtocolState, action: IAction<{ protocolId: number, name: string }>) =>
      {
        state.protocols.forEach(p =>
        {
          if(p.protocolId !== action.payload.protocolId)
            return;

          p.name = action.payload.name;
        });
      },
      updateChecked: (state: ProtocolState, action: IAction<{ protocolId: number, dosingId: number }>) =>
      {
        const dosing = state.protocols.find(p => p.protocolId === action.payload.protocolId)?.dosings
          ?.find(d => d.dosingId === action.payload.dosingId);
        if(!dosing)
          return;

        dosing.checked = !dosing.checked;
      },
      updateDosingNote: (state: ProtocolState, action: IAction<{
        protocolId: number, dosingId: number, note?: string }>) =>
      {
        const dosing = state.protocols.find(p => p.protocolId === action.payload.protocolId)?.dosings
          ?.find(d => d.dosingId === action.payload.dosingId);
        if(!dosing)
          return;

        dosing.note = action.payload.note;
      },
      updateProtocolBudget: (state: ProtocolState, action: IAction<{ protocolId: number, budget: number }>) =>
      {
        state.protocols.forEach(p =>
        {
          if(p.protocolId !== action.payload.protocolId)
            return;

          p.budget = action.payload.budget;
        });
      },
      updateDiscounts: (state: ProtocolState, action: IAction<{
        protocolId: number,
        dosingId: number,
        discounts: IDiscount[] }>) =>
      {
        const protocol = state.protocols.find(p => p.protocolId === action.payload.protocolId);
        if(!protocol)
          return;

        protocol.dosings?.forEach(dosing =>
        {
          if(dosing.dosingId !== action.payload.dosingId)
            return;

          dosing.discounts = action.payload.discounts;
        });
      },
      updateOrderReminder: (state: ProtocolState, action: IAction<{
        protocolId: number,
        orderReminder?: boolean }>) =>
      {
        const protocol = state.protocols.find(p => p.protocolId === action.payload.protocolId);
        if(!protocol)
          return;

        protocol.orderReminderActive = (action.payload.orderReminder === undefined) ?
          !protocol.orderReminderActive :
          action.payload.orderReminder;
      },
      addSharedWithUser: (state: ProtocolState, action: IAction<{
        protocolId: number,
        userId: number,
        username: string }>) =>
      {
        state.protocols.forEach(p =>
        {
          if(p.protocolId !== action.payload.protocolId)
            return;

          p.sharedWith = p.sharedWith.concat({ ...action.payload, edit: false });
        });
      },
      updateSharedWithUserEdit: (state: ProtocolState, action: IAction<{
        protocolId: number,
        userId: number,
        edit: boolean }>) =>
      {
        state.protocols.forEach(p =>
          p.protocolId === action.payload.protocolId &&
          p.sharedWith.forEach(u =>
          {
            if(u.userId !== action.payload.userId)
              return;

            u.edit = action.payload.edit;
          }));
      },
      removeSharedWithUser: (state: ProtocolState, action: IAction<{ protocolId: number, userId: number }>) =>
      {
        state.protocols = state.protocols
          .map(p =>  p.protocolId === action.payload.protocolId ?
            ({ ...p, sharedWith: p.sharedWith.filter(u => u.userId !== action.payload.userId) }) :
            p);
      },
      updateSavePublicProtocolId: (state: ProtocolState, action: IAction<number | undefined>) =>
      {
        state.savePublicProtocolId = action.payload;
      },
      updateSaveMmProtocolId: (state: ProtocolState, action: IAction<number | undefined>) =>
      {
        state.saveMmProtocolId = action.payload;
      },
      updateFolders: (state: ProtocolState, action: IAction<Folder[]>) =>
      {
        state.folders = action.payload;
        state.folders.sort((a, b) => alphabeticalSort(a.name, b.name));
      },
      addFolder: (state: ProtocolState, action: IAction<Folder>) =>
      {
        state.folders.push(action.payload);
      },
      updateFolderParentId: (state: ProtocolState, action: IAction<{
        folderId: number,
        parentFolderId?: number }>) =>
      {
        const folder = state.folders.find(f => f.folderId === action.payload.folderId);
        if(!folder)
          return;

        folder.parentFolderId = action.payload.parentFolderId;
      },
      renameFolder: (state: ProtocolState, action: IAction<{ folderId: number, name: string }>) =>
      {
        const folder = state.folders.find(f => f.folderId === action.payload.folderId);
        if(!folder)
          return;

        folder.name = action.payload.name;
      },
      removeFolder: (state: ProtocolState, action: IAction<number>) =>
      {
        state.folders = state.folders.filter(f => f.folderId !== action.payload);
      },
      updateFees: (state: ProtocolState, action: IAction<{
        protocolId: number,
        fees?: boolean }>) =>
      {
        const protocol = state.protocols.find(p => p.protocolId === action.payload.protocolId);
        if(!protocol)
          return;

        protocol.fees = (action.payload.fees === undefined) ? !protocol.fees : action.payload.fees;
      },
      updateCurrency: (state: ProtocolState, action: IAction<{
        protocolId: number,
        currencyId: number,
        currencyCode: string,
        currencySymbol: string }>) =>
      {
        const protocol = state.protocols.find(p => p.protocolId === action.payload.protocolId);
        if(!protocol)
          return;

        protocol.currencyId = action.payload.currencyId;
        protocol.currencyCode = action.payload.currencyCode;
        protocol.currencySymbol = action.payload.currencySymbol;
      },
    }
  });

export const {
  updateProtocols,
  updateProtocolPublic,
  updateProtocolFolder,
  addProtocol,
  removeProtocol,
  updateProtocolName,
  updateProtocolBudget,
  updateOrderReminder,
  updateDosings,
  removeDosings,
  updateSavePublicProtocolId,
  updateSaveMmProtocolId,
  updateFolders,
  addFolder,
  updateFolderParentId,
  renameFolder,
  removeFolder,
  updateChecked,
  updateDosingNote,
  updateDiscounts,
  addSharedWithUser,
  updateSharedWithUserEdit,
  removeSharedWithUser,
  updateFees,
  updateCurrency } = protocolSlice.actions;
export default protocolSlice.reducer;


